/**
 * Configuration — Pricing Plan (Paired Configuration)
 * Applies configurable rules to permanent Basic and Pro source records.
 * Samples as of 10 Aug 2026.
 */

import {
  PERMANENT_PLAN_ORDER,
  PLAN_KEY_BY_ID,
  PLAN_NAME_BY_ID,
  PRICE_CHANGE_NOTICE_DAYS,
  buildSamplePricingPlanManagementRecords,
  formatEffectiveFromDisplay,
  formatMonthlyCostFull,
  hasPendingPriceChange,
  resolveCurrentMonthlyCost,
} from './pricingPlanManagementData';

export { PRICE_CHANGE_NOTICE_DAYS };

export const BILLING_CYCLE = 'Monthly';
export const GST_DISPLAY = 'Included';

export const VISIBILITY_OPTIONS = ['Hidden', 'Active Verified Customers Only'];

export const YES_NO_OPTIONS = ['Yes', 'No'];

/** Fixed when Basic trial is Yes — not a free dropdown. */
export const PAYMENT_METHOD_DURING_TRIAL = 'Optional';

export const UPGRADE_TIMING_OPTIONS = ['Immediate'];
export const UPGRADE_CHARGE_OPTIONS = ['Prorated Difference'];
export const DOWNGRADE_TIMING_OPTIONS = ['End of Current Billing Cycle'];
export const CANCELLATION_TIMING_OPTIONS = ['End of Current Billing Cycle'];

export const CONDITIONS_STATEMENT = 'No Conditions';
export const DRAFT_POST_BEHAVIOUR = 'Does not count';

export const GRACE_ELIGIBILITY =
  'After at least one successful paid subscription payment';
export const REACTIVATION_PAYMENT = 'Full current monthly cost required to reactivate';
export const AUTOMATIC_POST_RESTORATION =
  'Previously Active Posts are restored automatically within allowance';

export const MINIMUM_PRICE_NOTICE_LABEL = '30 calendar days';
export const MINIMUM_PRICE_NOTICE_TABLE_LABEL = '30 cal. days';

export const SOURCE_PRICE_CHANGE_STATES = {
  NONE: 'None',
  PENDING_CONFIRMATION: 'Pending Confirmation',
  SCHEDULED: 'Scheduled',
  CANCELLATION_PENDING: 'Cancellation Pending Confirmation',
};

export const SAVE_SUCCESS_MESSAGE = 'Pricing Plan configuration saved successfully.';
export const SAVE_FAILURE_MESSAGE =
  'Pricing Plan configuration could not be saved. Review the highlighted fields and try again.';
export const SAVE_NO_CHANGE_MESSAGE = 'No changes to save.';

export const CONFIG_ID_PLACEHOLDER = 'Generated on Save';
export const SOURCE_PLAN_PLACEHOLDER = 'Select Source Plan';

export const PLAN_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
];

const SAMPLE_ACTOR = 'Saim Sahi';
const SAMPLE_AS_OF = '2026-08-10T09:00:00+10:00';

export const VALIDATION_MESSAGES = {
  sourcePlanRequired: 'Select Source Plan.',
  sourcePlanExists: 'A configuration already exists for this Pricing Plan.',
  requiredFields: 'Complete all required fields.',
  trialLengthInvalid: 'Enter a valid Basic Trial Length.',
  proTrialNotSupported: 'Pro does not support a trial.',
  wholeNumberZeroOrGreater: 'Enter a whole number of zero or greater.',
  quotesAllowedInvalid: 'Enter a whole number of zero or greater for Quotes Allowed.',
  contactDetailsRequired: 'Select Contact Details Visibility.',
  externalLinksRequired: 'Select Allowed External Links Visibility.',
  gracePeriodInvalid: 'Enter a valid Grace Period.',
  planChangeIncomplete: 'Complete the applicable plan-change settings.',
  cancellationRequired: 'Select Cancellation Timing.',
  yesNoRequired: 'Select Yes or No.',
  conditionsNotSupported: 'Pricing Plan configuration does not support Conditions.',
  unavailableValue: 'Replace the unavailable value.',
};

export const BASIC_TRIAL_LENGTH_DAYS = 28;
export const GRACE_PERIOD_DAYS = 7;
export const GRACE_PERIOD_LABEL = '7 Days';
export const SERVICE_POSTS_BASIC = 1;
export const SERVICE_POSTS_PRO = 5;
export const FEATURED_POSTS_BASIC = 0;
export const FEATURED_POSTS_PRO = 5;
export const QUOTES_ALLOWED_BASIC = 4;
export const QUOTES_ALLOWED_PRO = 15;
export const BILLING_CURRENCY = 'AUD';

export function getPlanKeyFromManagementPlan(managementPlan) {
  if (!managementPlan) return null;
  return PLAN_KEY_BY_ID[managementPlan.id] ?? null;
}

export function getDefaultPostsAllowed(planKey) {
  return planKey === 'pro' ? SERVICE_POSTS_PRO : SERVICE_POSTS_BASIC;
}

export function getDefaultFeaturedPostsAllowed(planKey) {
  return planKey === 'pro' ? FEATURED_POSTS_PRO : FEATURED_POSTS_BASIC;
}

export function getDefaultQuotesAllowed(planKey) {
  return planKey === 'pro' ? QUOTES_ALLOWED_PRO : QUOTES_ALLOWED_BASIC;
}

export function getDefaultVisibility(planKey) {
  return planKey === 'pro' ? 'Active Verified Customers Only' : 'Hidden';
}

export function buildSourceSnapshot(managementPlan) {
  if (!managementPlan) return null;
  const currentMonthlyCost = Number(
    managementPlan.currentMonthlyCost ?? managementPlan.monthlyCost,
  );
  const pendingRaw = managementPlan.pendingMonthlyCost;
  const pendingMonthlyCost =
    pendingRaw == null || pendingRaw === '' ? null : Number(pendingRaw);
  return {
    id: managementPlan.id || '',
    planName: managementPlan.planName || '',
    currentMonthlyCost: Number.isFinite(currentMonthlyCost) ? currentMonthlyCost : null,
    monthlyCost: Number.isFinite(currentMonthlyCost) ? currentMonthlyCost : null,
    pendingMonthlyCost: Number.isFinite(pendingMonthlyCost) ? pendingMonthlyCost : null,
    effectiveFrom: managementPlan.effectiveFrom || null,
    gstIncluded: managementPlan.gstIncluded !== false,
    billingCycle: managementPlan.billingCycle || BILLING_CYCLE,
    status: managementPlan.status || '',
    publicDescription: (managementPlan.publicDescription || '').trim(),
  };
}

export function labelPlanName(planKey) {
  if (planKey === 'pro') return 'Pro';
  if (planKey === 'basic') return 'Basic';
  return planKey || '—';
}

export function formatPlanCell(planId, planName) {
  const name = planName || PLAN_NAME_BY_ID[planId] || '—';
  return `${planId || '—'} · ${name}`;
}

export function formatDaysLabel(days) {
  if (days == null || days === '') return '—';
  const n = Number(days);
  if (!Number.isFinite(n)) return '—';
  return `${n} Day${n === 1 ? '' : 's'}`;
}

export function labelTrialForTable(config) {
  if (config?.planKey === 'pro') return 'No trial';
  if (config?.trialIncluded !== 'Yes') return 'No trial';
  const n = Number(config.trialLengthDays ?? BASIC_TRIAL_LENGTH_DAYS);
  if (!Number.isFinite(n)) return 'No trial';
  return `${n} days`;
}

export function labelPlanChangeForTable(config) {
  if (config?.planKey === 'pro') {
    return 'Downgrade: Cycle end';
  }
  return 'Upgrade: Immediate';
}

export function labelVisibilityForTable(value) {
  if (value === 'Active Verified Customers Only') return 'Verified only';
  if (value === 'Hidden') return 'Hidden';
  return value || '—';
}

export function deriveSourcePriceChangeState(config, managementPlan) {
  const sourcePending = hasPendingPriceChange(managementPlan);
  const scheduled = config?.operationalPriceSchedule;
  const hasScheduled =
    scheduled &&
    scheduled.pendingMonthlyCost != null &&
    scheduled.effectiveFrom;

  if (hasScheduled && !sourcePending) {
    return SOURCE_PRICE_CHANGE_STATES.CANCELLATION_PENDING;
  }
  if (sourcePending && !hasScheduled) {
    return SOURCE_PRICE_CHANGE_STATES.PENDING_CONFIRMATION;
  }
  if (
    sourcePending &&
    hasScheduled &&
    Number(scheduled.pendingMonthlyCost) === Number(managementPlan.pendingMonthlyCost) &&
    String(scheduled.effectiveFrom) === String(managementPlan.effectiveFrom)
  ) {
    return SOURCE_PRICE_CHANGE_STATES.SCHEDULED;
  }
  if (sourcePending && hasScheduled) {
    return SOURCE_PRICE_CHANGE_STATES.PENDING_CONFIRMATION;
  }
  return SOURCE_PRICE_CHANGE_STATES.NONE;
}

export function generatePublicFeatureContent(config) {
  if (!config) return [];
  const posts = Number(config.postsAllowed);
  const featured = Number(config.featuredPostsAllowed);
  const quotes = Number(config.quotesAllowed);
  const contact = config.contactDetailsVisibility || 'Hidden';
  const links = config.allowedExternalLinksVisibility || 'Hidden';
  const isPro = config.planKey === 'pro';

  const items = [];

  if (!isPro && config.trialIncluded === 'Yes') {
    const days = Number(config.trialLengthDays) || BASIC_TRIAL_LENGTH_DAYS;
    items.push(`${days}-day trial available once per ABN`);
  } else {
    items.push('No Trial');
  }

  if (Number.isFinite(posts)) {
    items.push(`${posts} Active Post${posts === 1 ? '' : 's'}`);
  }

  if (Number.isFinite(featured)) {
    if (featured > 0) {
      items.push(`${featured} additional Featured Post${featured === 1 ? '' : 's'}`);
    } else {
      items.push('No additional Featured Posts');
    }
  }

  if (Number.isFinite(quotes)) {
    items.push(`${quotes} Quotes per billing cycle`);
    if (!isPro && config.trialIncluded === 'Yes') {
      items.push(`Trial includes ${quotes} Quotes`);
    }
  }

  if (contact === 'Hidden') {
    items.push('Contact Details hidden');
  } else {
    items.push('Contact Details visible to Active Verified Customers only');
  }

  if (links === 'Hidden') {
    items.push('Allowed External Links hidden');
  } else {
    items.push('Allowed External Links visible to Active Verified Customers only');
  }

  return items;
}

export function formatPublicFeaturePreview(config) {
  return generatePublicFeatureContent(config).join('\n');
}

export function getQuoteAllowanceQualificationNote(config) {
  const quotes = Number(config?.quotesAllowed);
  if (!Number.isFinite(quotes)) {
    return 'Quote allowance applies per billing cycle. Unused quotes do not roll over.';
  }
  if (config?.planKey !== 'pro' && config?.trialIncluded === 'Yes') {
    return `Up to ${quotes} Quotes per billing cycle. The same allowance applies during trial. Unused quotes do not roll over.`;
  }
  return `Up to ${quotes} Quotes per billing cycle. Unused quotes do not roll over.`;
}

export function enrichConfigurationRecord(config, managementPlan) {
  const liveSource = managementPlan ? buildSourceSnapshot(managementPlan) : config?.source || null;
  const planKey =
    config?.planKey ||
    getPlanKeyFromManagementPlan(managementPlan) ||
    PLAN_KEY_BY_ID[config?.planId] ||
    '';

  return {
    ...config,
    planKey,
    planId: config?.planId || managementPlan?.id || '',
    planName: managementPlan?.planName || config?.planName || labelPlanName(planKey),
    source: liveSource || config?.source || null,
    planStatus: managementPlan?.status || config?.planStatus || '',
    conditions: Array.isArray(config?.conditions) ? config.conditions : [],
    sourcePriceChangeState: deriveSourcePriceChangeState(config, managementPlan || liveSource),
    publicFeatureContent: generatePublicFeatureContent({ ...config, planKey }),
    changeHistory: Array.isArray(config?.changeHistory) ? config.changeHistory : [],
  };
}

export function enrichConfigurationRecords(configs, managementPlans) {
  const plans = managementPlans || [];
  return (configs || []).map((config) => {
    const plan = plans.find((p) => p.id === config.planId) || null;
    return enrichConfigurationRecord(config, plan);
  });
}

export function findConfigurationById(configurations, configId) {
  return (configurations || []).find((config) => config.id === configId) ?? null;
}

export function findConfigurationByPlanId(configurations, planId) {
  return (
    (configurations || []).find(
      (config) => config.planId === planId || config.id === planId,
    ) ?? null
  );
}

export function canAddPricingPlanConfiguration(configurations, managementPlans) {
  const paired = new Set((configurations || []).map((c) => c.planId));
  return (managementPlans || []).some((plan) => !paired.has(plan.id));
}

export function getAvailableSourcePlans(configurations, managementPlans, excludeConfigId) {
  const paired = new Set(
    (configurations || [])
      .filter((c) => c.id !== excludeConfigId)
      .map((c) => c.planId),
  );
  return (managementPlans || []).filter((plan) => !paired.has(plan.id));
}

export function matchesConfigurationPlanFilter(config, planFilter) {
  if (planFilter == null || planFilter === '' || planFilter === 'all') return true;
  return config?.planKey === planFilter;
}

export function generateNextConfigurationId(configurations) {
  const max = (configurations || []).reduce((acc, config) => {
    const match = String(config.id || '').match(/^PPC-(\d+)$/i);
    if (!match) return acc;
    return Math.max(acc, Number(match[1]));
  }, 0);
  return `PPC-${String(max + 1).padStart(3, '0')}`;
}

export function getDefaultConfigurationFields(planKey) {
  const visibility = getDefaultVisibility(planKey);
  const isPro = planKey === 'pro';
  return {
    trialIncluded: isPro ? 'No' : 'Yes',
    trialLengthDays: isPro ? null : BASIC_TRIAL_LENGTH_DAYS,
    paymentMethodDuringTrial: isPro ? '' : PAYMENT_METHOD_DURING_TRIAL,
    postsAllowed: getDefaultPostsAllowed(planKey),
    featuredPostsAllowed: getDefaultFeaturedPostsAllowed(planKey),
    quotesAllowed: getDefaultQuotesAllowed(planKey),
    contactDetailsVisibility: visibility,
    allowedExternalLinksVisibility: visibility,
    gracePeriodDays: GRACE_PERIOD_DAYS,
    upgradeTiming: isPro ? '' : UPGRADE_TIMING_OPTIONS[0],
    upgradeChargeMethod: isPro ? '' : UPGRADE_CHARGE_OPTIONS[0],
    downgradeTiming: isPro ? DOWNGRADE_TIMING_OPTIONS[0] : '',
    cancellationTiming: CANCELLATION_TIMING_OPTIONS[0],
    conditions: [],
    operationalPriceSchedule: null,
  };
}

function stringifyWholeNumberField(value) {
  return value != null && value !== '' ? String(value) : '';
}

export function emptyConfigurationForm() {
  return {
    id: '',
    planId: '',
    planKey: '',
    planName: '',
    source: null,
    trialIncluded: '',
    trialLengthDays: '',
    paymentMethodDuringTrial: '',
    postsAllowed: '',
    featuredPostsAllowed: '',
    quotesAllowed: '',
    contactDetailsVisibility: '',
    allowedExternalLinksVisibility: '',
    gracePeriodDays: '',
    upgradeTiming: '',
    upgradeChargeMethod: '',
    downgradeTiming: '',
    cancellationTiming: '',
    conditions: [],
    operationalPriceSchedule: null,
    createdOn: '',
    createdBy: '',
    lastUpdatedOn: '',
    lastUpdatedBy: '',
    changeHistory: [],
  };
}

export function toConfigurationForm(config) {
  if (!config) return emptyConfigurationForm();
  return {
    id: config.id || '',
    planId: config.planId || '',
    planKey: config.planKey || '',
    planName: config.planName || '',
    source: config.source ? { ...config.source } : null,
    trialIncluded: config.trialIncluded || '',
    trialLengthDays: stringifyWholeNumberField(config.trialLengthDays),
    paymentMethodDuringTrial: config.paymentMethodDuringTrial || '',
    postsAllowed: stringifyWholeNumberField(config.postsAllowed),
    featuredPostsAllowed: stringifyWholeNumberField(config.featuredPostsAllowed),
    quotesAllowed: stringifyWholeNumberField(config.quotesAllowed),
    contactDetailsVisibility: config.contactDetailsVisibility || '',
    allowedExternalLinksVisibility: config.allowedExternalLinksVisibility || '',
    gracePeriodDays: stringifyWholeNumberField(config.gracePeriodDays),
    upgradeTiming: config.upgradeTiming || '',
    upgradeChargeMethod: config.upgradeChargeMethod || '',
    downgradeTiming: config.downgradeTiming || '',
    cancellationTiming: config.cancellationTiming || '',
    conditions: Array.isArray(config.conditions) ? [...config.conditions] : [],
    operationalPriceSchedule: config.operationalPriceSchedule
      ? { ...config.operationalPriceSchedule }
      : null,
    createdOn: config.createdOn || '',
    createdBy: config.createdBy || '',
    lastUpdatedOn: config.lastUpdatedOn || config.lastConfirmedOn || '',
    lastUpdatedBy: config.lastUpdatedBy || config.lastConfirmedBy || '',
    changeHistory: Array.isArray(config.changeHistory) ? [...config.changeHistory] : [],
  };
}

/**
 * Apply Source Plan selection on Add — resets configurable fields to plan defaults.
 */
export function applySourcePlanChange(prevForm, managementPlan) {
  const planId = managementPlan?.id || '';
  const planKey = getPlanKeyFromManagementPlan(managementPlan) || '';
  const defaults = planKey ? getDefaultConfigurationFields(planKey) : {};
  return {
    ...prevForm,
    planId,
    planKey,
    planName: managementPlan?.planName || '',
    source: managementPlan ? buildSourceSnapshot(managementPlan) : null,
    ...defaults,
    trialLengthDays:
      defaults.trialLengthDays != null ? String(defaults.trialLengthDays) : '',
    paymentMethodDuringTrial: defaults.paymentMethodDuringTrial || '',
    postsAllowed: defaults.postsAllowed != null ? String(defaults.postsAllowed) : '',
    featuredPostsAllowed:
      defaults.featuredPostsAllowed != null ? String(defaults.featuredPostsAllowed) : '',
    quotesAllowed: defaults.quotesAllowed != null ? String(defaults.quotesAllowed) : '',
    gracePeriodDays: defaults.gracePeriodDays != null ? String(defaults.gracePeriodDays) : '',
    conditions: [],
  };
}

function buildSampleConfig({
  id,
  planId,
  planKey,
  managementPlan,
  createdOn,
  overrides = {},
}) {
  const defaults = getDefaultConfigurationFields(planKey);
  const source = buildSourceSnapshot(managementPlan);
  return enrichConfigurationRecord(
    {
      id,
      planId,
      planKey,
      planName: managementPlan?.planName || labelPlanName(planKey),
      source,
      ...defaults,
      ...overrides,
      conditions: [],
      operationalPriceSchedule: null,
      createdOn,
      createdBy: SAMPLE_ACTOR,
      lastUpdatedOn: SAMPLE_AS_OF,
      lastUpdatedBy: SAMPLE_ACTOR,
      changeHistory: [
        {
          action: 'Created',
          by: SAMPLE_ACTOR,
          on: createdOn,
          previousValues: null,
          newValues: null,
        },
      ],
    },
    managementPlan,
  );
}

export function buildSamplePricingPlanConfigurations(managementPlans) {
  const plans = managementPlans || buildSamplePricingPlanManagementRecords();
  const basicPlan = plans.find((plan) => plan.id === 'PLAN-001');
  const proPlan = plans.find((plan) => plan.id === 'PLAN-002');

  return [
    buildSampleConfig({
      id: 'PPC-001',
      planId: 'PLAN-001',
      planKey: 'basic',
      managementPlan: basicPlan,
      createdOn: '2026-07-06T10:35:00+10:00',
      overrides: {
        trialIncluded: 'Yes',
        trialLengthDays: BASIC_TRIAL_LENGTH_DAYS,
        paymentMethodDuringTrial: PAYMENT_METHOD_DURING_TRIAL,
        postsAllowed: SERVICE_POSTS_BASIC,
        featuredPostsAllowed: FEATURED_POSTS_BASIC,
        quotesAllowed: QUOTES_ALLOWED_BASIC,
        contactDetailsVisibility: 'Hidden',
        allowedExternalLinksVisibility: 'Hidden',
        gracePeriodDays: GRACE_PERIOD_DAYS,
        upgradeTiming: UPGRADE_TIMING_OPTIONS[0],
        upgradeChargeMethod: UPGRADE_CHARGE_OPTIONS[0],
      },
    }),
    buildSampleConfig({
      id: 'PPC-002',
      planId: 'PLAN-002',
      planKey: 'pro',
      managementPlan: proPlan,
      createdOn: '2026-07-06T11:30:00+10:00',
      overrides: {
        trialIncluded: 'No',
        trialLengthDays: null,
        paymentMethodDuringTrial: '',
        postsAllowed: SERVICE_POSTS_PRO,
        featuredPostsAllowed: FEATURED_POSTS_PRO,
        quotesAllowed: QUOTES_ALLOWED_PRO,
        contactDetailsVisibility: 'Active Verified Customers Only',
        allowedExternalLinksVisibility: 'Active Verified Customers Only',
        gracePeriodDays: GRACE_PERIOD_DAYS,
        downgradeTiming: DOWNGRADE_TIMING_OPTIONS[0],
      },
    }),
  ];
}

export function buildPricingPlanListRows(managementPlans, configurations) {
  const plans = managementPlans || [];
  return enrichConfigurationRecords(configurations, plans).sort((a, b) => {
    const ai = PERMANENT_PLAN_ORDER.indexOf(a.planId);
    const bi = PERMANENT_PLAN_ORDER.indexOf(b.planId);
    if (ai === -1 && bi === -1) return String(a.id).localeCompare(String(b.id));
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

export function formatPendingCostDisplay(managementPlan) {
  if (!hasPendingPriceChange(managementPlan)) return '—';
  return formatMonthlyCostFull(managementPlan.pendingMonthlyCost);
}

export function formatEffectiveFromOrDash(managementPlan) {
  if (!hasPendingPriceChange(managementPlan)) return '—';
  return formatEffectiveFromDisplay(managementPlan.effectiveFrom);
}

export function formatCurrentCostDisplay(managementPlan) {
  return formatMonthlyCostFull(resolveCurrentMonthlyCost(managementPlan));
}
